'use strict';

const libWorkspace = require('pict-section-workspace');

/**
 * ObservationWorkspaceProvider — the engine behind the Observation view (#/Observation/Workspace/:ID[/:Tab], and the
 * bespoke bridge over /PSRS/Observation/View/:GUID). A subclass of the pict-section-workspace base: the base owns the
 * shell (header, tab bar, routing, record load); this subclass adds the four bespoke `render`-kind tab bodies that
 * present a field-captured observation:
 *   • Data       — visualization OF the data: the composited image (annotations burned in) + the per-artifact
 *                  captured data (from the fetched Observation's `.Details`, keyed by manifest slot) + labels.
 *   • Location   — WHERE it was captured: Capture (device/EXIF) + User (person) coordinates on a read-only Leaflet
 *                  map (the ObservationLocationMap instance), combined via fitBounds.
 *   • History    — older VERSIONS: the ObservationArchive snapshots (one per save), newest first, each previewable.
 *   • Artifacts  — different VERSIONS of each artifact's raw media (1..Artifact.Version).
 *
 * Read-only for now; roll-back / roll-forward / merge build on this later (PUT a chosen SnapshotJSON + re-version
 * artifact media). Config in [[Observation.config.js]]; map instance registered in PlatformConfiguration-Application-Web.
 */
class ObservationWorkspaceProvider extends libWorkspace.HeadlightWorkspaceBaseProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		/** @type {any} */
		this.pict;
		this._obs = null;
		this._details = {};
		this._labels = {};
		this._points = { capture: null, user: null };
		this._creatorName = '';
		this._tabs = {};
	}

	get idObservation() { return this._obs ? this._obs.IDObservation : null; }

	// Map contract (the ObservationLocationMap reads these; showForProject is overridden to use mapPoints()).
	get center() { const p = this._points && (this._points.capture || this._points.user); return p ? { Latitude: p.lat, Longitude: p.lng } : { Latitude: null, Longitude: null }; }
	get hasCenter() { return !!(this._points && (this._points.capture || this._points.user)); }
	mapPoints() { return this._points; }

	// --- small helpers ---
	_get(pURI) { return this._getJSON(pURI); }   // base _getJSON resolves null on error
	_mediaUrl(pPath) { const rc = this.restClient; if (rc && (typeof rc.formatUrl === 'function')) { try { return rc.formatUrl(pPath); } catch (e) { /* fall through */ } } return `/1.0/${pPath}`; }
	_obsImagePath(pSize, pVersion) { const tmpBase = `Observation/${this.idObservation}/Image/${pSize}`; return (pVersion != null && pVersion !== '') ? `${tmpBase}/V/${pVersion}` : tmpBase; }
	_artifactMediaPath(pIDArtifact, pVersion) { return `Artifact/Media/${Number(pIDArtifact)}/${Number(pVersion) || 1}`; }
	_prettySlot(pSlot) { return String(pSlot || '').replace(/([a-z])([A-Z])/g, '$1 $2'); }
	_fmtDate(pValue) { if (!pValue) { return ''; } try { const d = new Date(pValue); if (!Number.isNaN(d.getTime())) { return d.toLocaleString(); } } catch (e) { /* fall through */ } return String(pValue).replace('T', ' ').slice(0, 16); }

	/** parse the two lat/long pairs (decimal strings; 0/near-0 = "no location" sentinel). */
	_computePoints(pObs)
	{
		const fPoint = (pLat, pLng) =>
		{
			const tmpLat = parseFloat(pLat); const tmpLng = parseFloat(pLng);
			if (!Number.isFinite(tmpLat) || !Number.isFinite(tmpLng)) { return null; }
			if ((Math.abs(tmpLat) < 0.0000001) && (Math.abs(tmpLng) < 0.0000001)) { return null; }
			return { lat: tmpLat, lng: tmpLng };
		};
		return { capture: fPoint(pObs.CaptureLatitude, pObs.CaptureLongitude), user: fPoint(pObs.UserLatitude, pObs.UserLongitude) };
	}

	async _lookupUserName(pID)
	{
		if (!pID) { return ''; }
		const r = await this._get(`User/${Number(pID)}`);
		if (r && !r.Error) { return [ r.NameFirst, r.NameLast ].filter(Boolean).join(' ') || r.Name || `#${pID}`; }
		return `#${pID}`;
	}

	/** The centralized observation renderer (registered in PlatformConfiguration-Application-Web). */
	get _obsRender() { return this.pict && this.pict.HeadlightObservationRender; }

	// --- hydrate: the base loaded the scalar record; re-fetch the full observation WITH its assembled .Details ---
	async hydrateAsync(pRecord)
	{
		this._tabs = {};
		const tmpFull = await this._get(`Observation/${pRecord.IDObservation}`);
		this._obs = (tmpFull && !tmpFull.Error) ? tmpFull : pRecord;
		this._details = (this._obs.Details && (typeof this._obs.Details === 'object')) ? this._obs.Details : {};
		this._labels = (this._obs.Labels && (typeof this._obs.Labels === 'object')) ? this._obs.Labels : {};
		this._points = this._computePoints(this._obs);
		this._creatorName = await this._lookupUserName(this._obs.CreatingIDUser);
		// Resolve the observation's schema (built-in OR custom — the API merges them) so the render service can lay out
		// columns/labels; a failure is non-fatal (it falls back to key-derived columns).
		this._spec = null;
		const tmpRender = this._obsRender;
		if (tmpRender && this._obs && this._obs.ObservationType && (typeof tmpRender.loadSpecification === 'function'))
		{
			await new Promise((resolve) => tmpRender.loadSpecification(this._obs.ObservationType, this._obs.IDProject,
				(pError, pSpec) => { this._spec = pError ? null : pSpec; resolve(); }));
		}
	}

	// --- Tab data (History loads on enter; Data/Location/Artifacts render from the already-hydrated record) ---
	_tabState(pTab) { return this._tabs[pTab] || {}; }
	_loadingOrEmpty(pTab, pEmptyMsg)
	{
		const t = this._tabState(pTab);
		if (t.Loading || (!t.Loaded && !t.Error)) { return `<div class="ow-note"><i class="fas fa-circle-notch fa-spin"></i> Loading…</div>`; }
		if (t.Error) { return `<div class="ow-error">${this._esc(t.Error)}</div>`; }
		if (!t.Rows || !t.Rows.length) { return `<div class="ow-note">${this._esc(pEmptyMsg)}</div>`; }
		return null;
	}

	async _ensureTabDataAsync(pTab)
	{
		if (!this._obs) { return; }
		if (this._tabs[pTab] && this._tabs[pTab].Loaded) { return; }
		this._tabs[pTab] = { Loading: true, Loaded: false };
		this.repaintBody();
		try
		{
			if (pTab === 'history') { this._tabs[pTab].Rows = await this._loadHistory(); }
			this._tabs[pTab].Loaded = true;
		}
		catch (e) { this._tabs[pTab].Error = (e && e.message) || String(e); this._tabs[pTab].Loaded = true; }
		finally { this._tabs[pTab].Loading = false; this.repaintBody(); }
	}

	async _loadHistory()
	{
		const tmpRows = await this._get(`ObservationArchives/FilteredTo/FBV~IDObservation~EQ~${this.idObservation}~FBV~Deleted~EQ~0~FSF~Version~DESC~0/0/100`);
		return Array.isArray(tmpRows) ? tmpRows : [];
	}

	// =====================================================================================================
	// Tab 1 — DATA (visualization of the captured data)
	// =====================================================================================================
	_dataHTML()
	{
		const tmpRender = this._obsRender;
		if (tmpRender && this._obs && (typeof tmpRender.renderObservationHTML === 'function'))
		{
			// The centralized service owns the whole Data body (hero + meta + description + captured-data slots + labels).
			// It is schema-driven: every artifact in every slot renders (spreadsheets as tables, journals as prose, media
			// as thumbnails/links) — replacing the old first-artifact-only walker that dropped rows and blanked spreadsheets.
			return tmpRender.renderObservationHTML(this._obs, { format: 'full', specification: this._spec || null, creatorName: this._creatorName });
		}
		return '<div class="ow-note">The observation renderer is unavailable.</div>';
	}

	// =====================================================================================================
	// Tab 2 — LOCATION (Capture + User on a map)
	// =====================================================================================================
	_locationHTML()
	{
		return `<div class="ow-loc">
			<div class="ow-loc-legend"><span class="ow-dot ow-dot-cap"></span> Capture <span class="ow-muted">(device / EXIF)</span> &nbsp;&nbsp; <span class="ow-dot ow-dot-usr"></span> User <span class="ow-muted">(person)</span></div>
			<div id="OW-MapHost" class="ow-map"></div>
			<div class="ow-loc-readout">${this._locReadoutHTML()}</div>
		</div>`;
	}

	_locReadoutHTML()
	{
		const p = this._points || {};
		const fmt = (x) => x ? `${x.lat.toFixed(6)}, ${x.lng.toFixed(6)}` : '<span class="ow-muted">Not recorded</span>';
		return `<div class="ow-loc-row"><span class="ow-loc-k">Capture</span><span class="ow-loc-v">${fmt(p.capture)}</span></div>`
			+ `<div class="ow-loc-row"><span class="ow-loc-k">User</span><span class="ow-loc-v">${fmt(p.user)}</span></div>`
			+ ((!p.capture && !p.user) ? `<div class="ow-note" style="margin-top:0.5rem">No location was recorded for this observation.</div>` : '');
	}

	/** Location tab OnEnter: (re)mount the read-only 2-marker map into the freshly-painted #OW-MapHost. */
	_mountMap()
	{
		const tmpCA = this.pict.ContentAssignment;
		if (tmpCA && (tmpCA.getElement('#OW-MapHost').length > 0))
		{
			const tmpMap = this.pict.views.ObservationLocationMap;
			if (tmpMap && (typeof tmpMap.showForProject === 'function')) { tmpMap.showForProject(); }
		}
	}

	// =====================================================================================================
	// Tab 3 — HISTORY (ObservationArchive versions)
	// =====================================================================================================
	_historyHTML()
	{
		const tmpBusy = this._loadingOrEmpty('history', 'No version history recorded for this observation yet.');
		if (tmpBusy) { return tmpBusy; }
		const tmpCurrent = this._obs ? Number(this._obs.Version) : null;
		const tmpRows = this._tabState('history').Rows.map((a) =>
		{
			const v = a.Version;
			const tmpWho = a.UpdatingIDUser || a.CreatingIDUser || '';
			const tmpIsCur = (Number(v) === tmpCurrent);
			return `<tr>
				<td class="ow-num">v${this._esc(v)}${tmpIsCur ? ' <span class="ow-badge ow-badge-cur">current</span>' : ''}</td>
				<td>${this._esc(this._fmtDate(a.UpdateDate || a.CreateDate))}</td>
				<td class="ow-mono">#${this._esc(tmpWho)}</td>
				<td><img class="ow-vthumb" src="${this._mediaUrl(this._obsImagePath('Thumbnail', v))}" loading="lazy" onerror="this.classList.add('ow-broken')"/></td>
			</tr>`;
		}).join('');
		return `<div class="ow-hint">Every save writes a full snapshot to the archive. Newest first; the preview is that version's rendered image (annotations included).</div>`
			+ `<table class="ow-table"><thead><tr><th>Version</th><th>When</th><th>By</th><th>Preview</th></tr></thead><tbody>${tmpRows}</tbody></table>`;
	}

	// =====================================================================================================
	// Tab 4 — ARTIFACT VERSIONS (raw media 1..Version per artifact)
	// =====================================================================================================
	_gatherArtifacts()
	{
		const tmpSeen = {}; const tmpOut = [];
		Object.keys(this._details).forEach((pSlot) =>
		{
			const arr = this._details[pSlot];
			(Array.isArray(arr) ? arr : []).forEach((a) =>
			{
				if (a && a.IDArtifact && !tmpSeen[a.IDArtifact])
				{
					tmpSeen[a.IDArtifact] = true;
					tmpOut.push({ IDArtifact: a.IDArtifact, ArtifactType: a.ArtifactType || pSlot, Version: Number(a.Version) || 1, Slot: pSlot });
				}
			});
		});
		return tmpOut;
	}

	_artifactsHTML()
	{
		const tmpArts = this._gatherArtifacts();
		if (!tmpArts.length) { return `<div class="ow-note">No artifacts are attached to this observation.</div>`; }
		const tmpCards = tmpArts.map((a) =>
		{
			const tmpIsImage = (a.ArtifactType === 'Image');
			const tmpTiles = [];
			for (let v = 1; v <= a.Version; v++)
			{
				const tmpCur = (v === a.Version);
				const tmpUrl = this._mediaUrl(this._artifactMediaPath(a.IDArtifact, v));
				if (tmpIsImage)
				{
					tmpTiles.push(`<div class="ow-vtile${tmpCur ? ' ow-vtile-cur' : ''}"><img class="ow-vtile-img" src="${tmpUrl}" loading="lazy" onerror="this.parentNode.classList.add('ow-broken')"/><div class="ow-vtile-cap">v${v}${tmpCur ? ' · current' : ''}</div></div>`);
				}
				else
				{
					tmpTiles.push(`<div class="ow-vtile${tmpCur ? ' ow-vtile-cur' : ''}"><a class="ow-vtile-file" href="${tmpUrl}" target="_blank" rel="noopener"><i class="fas fa-file"></i></a><div class="ow-vtile-cap">v${v}${tmpCur ? ' · current' : ''}</div></div>`);
				}
			}
			return `<div class="ow-art"><div class="ow-art-h">${this._esc(this._prettySlot(a.Slot))}<span class="ow-badge">${this._esc(a.ArtifactType)}</span><span class="ow-muted">${a.Version} version${a.Version > 1 ? 's' : ''}</span></div><div class="ow-vtiles">${tmpTiles.join('')}</div></div>`;
		}).join('');
		return `<div class="ow-hint">Each artifact's raw media versions, oldest → current. Annotation edits are tracked at the observation level (see <b>History</b>) and are NOT burned into this raw media.</div>${tmpCards}`;
	}
}

module.exports = ObservationWorkspaceProvider;
